/**
 * @file WebSocketSyncAdapter.js
 * @description Tier 2 sync adapter — LAN / multi-machine via Connect WebSocket relay. After the pulse
 * migration this adapter carries only PRESENCE (join / peers / conductor election) plus the
 * raw-message TEE (`onRawMessage`/`sendRaw`) that the room pulse rides. The conductor TEMPO channel
 * (sync:timeline/heartbeat) + the x/y/z param mirror (sync:param) + their RTT latency-correction are
 * removed — tempo replicates leaderlessly over the pulse's own relay (ConnectRelay, which does its own
 * ping/offset over the tee), not here.
 *
 * Protocol (additive to existing Connect messages — handled before the standard register/registered flow):
 *
 *   sync:register    — first message on open; identifies this as a sync-only connection
 *   sync:join        — join a named session; server replies sync:joined
 *   sync:joined      — server confirms join, sends the peer list
 *   sync:peer-joined — server broadcasts when a new peer joins
 *   sync:peer-left   — server broadcasts when a peer leaves or disconnects
 *   sync:leave       — graceful leave
 *
 * Leader election: same as BroadcastChannelAdapter — first active peer is the conductor. `isConductor`
 * is surfaced for the on-screen role label + the iframe export; it no longer gates any tempo path.
 */

const RECONNECT_DELAY_MS = 3_000;
// Giving up is terminal — it tells the room client the session is gone, and the participant is out
// until they reconnect by hand. That limit was previously unreachable, so making it reachable is
// what decides how patient we are. With the backoff below, this budget spans about three minutes of
// continuous failure before we call it: long enough that a phone changing networks, or a relay
// restart, is ridden out rather than treated as the end of the session.
const MAX_RECONNECT_ATTEMPTS = 8;
// A socket must stay open at least this long to count as a real recovery. Opening is not
// succeeding: a flapping connection that opens and dies immediately would otherwise hand itself a
// fresh attempt budget every time and retry forever, and every retry re-sends the join, which
// re-arms every deck's load barrier. Surviving this long means we genuinely reconnected.
const CONNECTION_STABLE_MS = 10_000;
// Retries back off 3s → 6s → 12s → 24s → 30s (capped from there), so a server or link that is down
// does not take a steady 20 requests/minute from every tab that is waiting on it.
const MAX_RECONNECT_DELAY_MS = 30_000;
// Spread of the random delay added to each retry, so tabs dropped by the same server outage do not
// all come back in the same instant.
const RECONNECT_JITTER_MS = 1_000;

export class WebSocketSyncAdapter {
  #ws = null;
  #wsUrl = null;
  #sessionId = 'orbiters-default';
  // peerId + joinedAt are app metadata for conductor election, sent to server as-is
  #peerId = `peer-${Math.random().toString(36).slice(2, 9)}`;
  #joinedAt = Date.now();
  #isConductor = false;
  #knownConductorId = null;
  #joinedExistingSession = false;
  #peers = new Map(); // peerId → joinedAt
  #peerVoiceCounts = new Map(); // peerId → that peer's synced-voice count (for the SYNC badge total)
  #registered = false; // true once server responds with sync:registered
  #peerCountCallback = null;
  #rawMessageCallbacks = new Set(); // Tee — fired for every inbound parsed message
  // The last `sync:joined` snapshot, replayed to tee subscribers that arrive after it (see onRawMessage).
  #lastJoinedMessage = null;
  #sendQueue = []; // messages buffered while WS is connecting
  #reconnectAttempts = 0;
  #reconnectTimer = null; // pending auto-reconnect setTimeout — cancelled on disconnect/destroy
  #openedAt = 0; // when the live socket opened — a short-lived open does not refund the attempt budget
  #destroyed = false;

  // Room identity: the stable per-tab clientId (+ displayName, optional userId, and the
  // server-minted reclaimToken once one exists) sent on register. Null outside room mode — the
  // register message then stays byte-identical to the presence-only form.
  #identity = null;

  /**
   * @param {string} wsUrl — WebSocket URL, e.g. from import.meta.env.VITE_WS_CONNECT
   * @param {string} [sessionId] — sync session name; defaults to 'orbiters-default'
   * @param {{ clientId: string, displayName?: string|null, userId?: string|null,
   *   reclaimToken?: string|null, whenReady?: Promise<unknown> }|null} [identity]
   *   room identity carried on `sync:register`. The relay needs a displayName to seat this
   *   connection as a player (registering without one makes it a spectator). Fields are read at
   *   send time, so a getter-backed object lets a reconnect present the latest adopted values.
   *   `whenReady` (optional) delays the FIRST register until the identity has settled — e.g. the
   *   bounded logged-in-account resolution; once settled, reconnect registers go out immediately.
   */
  constructor(wsUrl, sessionId = 'orbiters-default', identity = null) {
    this.#wsUrl = wsUrl;
    this.#sessionId = sessionId;
    this.#identity = identity && typeof identity === 'object' ? identity : null;
  }

  /** @param {(count: number) => void} cb */
  onPeerCount(cb) { this.#peerCountCallback = cb; }

  /**
   * Additive tee: subscribe to EVERY inbound parsed message, in addition to
   * the existing typed handling below. Lets the shared clock (ConnectRelay) ride this
   * SAME Connect socket — one client = one Connect peer — instead of opening a 2nd
   * connection. Does not affect existing callbacks. Returns an unsubscribe fn.
   * @param {(msg: object) => void} cb
   * @returns {() => void}
   */
  onRawMessage(cb) {
    this.#rawMessageCallbacks.add(cb);
    // LATE-SUBSCRIBER CATCH-UP. `sync:joined` is sent once, right after registration. The shared-clock
    // pulse and its ConnectRelay are built LAZILY (they need an audio engine, and in a room the decks
    // start empty), so they subscribe long after that message has gone past — and then wait forever
    // for one that will never come again. The result is a pulse that is wired but never joins, a
    // session epoch that is never seeded, and therefore no shared beat for anyone to align to.
    // Replaying the last join snapshot to whoever subscribes after it costs nothing and is exactly
    // the state they missed. Async so the subscriber is fully constructed before it arrives.
    if (this.#lastJoinedMessage) {
      const snapshot = this.#lastJoinedMessage;
      queueMicrotask(() => {
        if (!this.#rawMessageCallbacks.has(cb)) return; // unsubscribed in the meantime
        try { cb(snapshot); } catch (error) { console.warn('[SyncAdapter:WS] raw replay failed', error); }
      });
    }
    return () => this.#rawMessageCallbacks.delete(cb);
  }

  /**
   * Additive raw send: transmit an arbitrary JSON-serializable message on this
   * socket, buffering until session join completes (same buffering the typed path uses).
   * Lets ConnectRelay publish its sync:beat / sync:ping envelopes over the shared socket.
   * @param {object} obj
   */
  sendRaw(obj) {
    if (this.#ws?.readyState === WebSocket.OPEN && this.#registered) {
      try {
        this.#ws.send(JSON.stringify(obj));
      } catch (e) {
        console.warn('[SyncAdapter:WS] sendRaw failed:', e);
      }
    } else if (!this.#destroyed) {
      // Not yet registered/joined — buffer; flushed by the sync:registered handler.
      this.#sendQueue.push(obj);
    }
  }

  /**
   * Opens the WebSocket and announces this peer to the sync session.
   * Returns true immediately (connection is async); messages are queued
   * until the socket opens.
   * @returns {boolean} false if WebSocket is unavailable or URL is missing
   */
  connect() {
    if (typeof WebSocket === 'undefined') {
      console.warn('[SyncAdapter:WS] WebSocket not available.');
      return false;
    }
    if (!this.#wsUrl) {
      console.warn('[SyncAdapter:WS] No WebSocket URL configured (VITE_WS_CONNECT).');
      return false;
    }
    // Re-enable after a disconnect() (sync toggle off→on) without rebuilding the adapter.
    this.#destroyed = false;
    // Double-connect guard: a socket already open or connecting needs no second open.
    if (this.#ws && (this.#ws.readyState === WebSocket.OPEN || this.#ws.readyState === WebSocket.CONNECTING)) {
      return true;
    }
    // Drop any retry still pending. We are opening a socket right now, and letting the old timer
    // fire later would tear down the connection we are about to establish — the reopen detaches the
    // live socket, so peers would see a leave and rejoin and every deck barrier would re-arm. The
    // backoff makes that window long enough to matter.
    clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
    // An explicit connect is the user asking again, so it starts from a full attempt budget even if
    // the previous run exhausted one. Only the automatic retries have to earn their refund — and
    // only a connect that actually opens a socket refunds, not one the guard above short-circuits.
    this.#reconnectAttempts = 0;
    this.#openSocket();
    return true;
  }

  /**
   * Leave the session but stay RE-CONNECTABLE (sync toggle off, B4): send sync:leave so peers get a
   * sync:peer-left and decrement their room count, close the socket, and stop the loops — WITHOUT the
   * permanent teardown of destroy() (raw-message subscribers + the room-pulse wiring survive, so a later
   * connect() + pulse.rejoin() re-joins cleanly). `#destroyed` suppresses the auto-reconnect in onclose.
   */
  disconnect() {
    this.#destroyed = true; // suppress onclose auto-reconnect; connect() clears it
    clearTimeout(this.#reconnectTimer); // cancel any pending auto-reconnect (else it would re-join while OFF)
    this.#reconnectTimer = null;
    this.#send({ type: 'sync:leave', sessionId: this.#sessionId }); // tell peers we left BEFORE detaching
    this.#detachSocket(); // detach handlers + close + null #ws (a stale onclose can't re-open)
    this.#registered = false;
    this.#peers.clear();
    this.#peerVoiceCounts.clear();
    this.#isConductor = false;
    this.#knownConductorId = null;
    this.#joinedExistingSession = false;
    this.#sendQueue = [];
    // A fresh sync:joined follows the next connect(); replaying the OLD one to a late subscriber
    // meanwhile would announce a session this socket is no longer in.
    this.#lastJoinedMessage = null;
    // NOTE: #rawMessageCallbacks deliberately preserved (the room pulse stays subscribed across a toggle).
  }

  get peerCount() { return this.#peers.size; }
  get isConductor() { return this.#isConductor; }

  /** This connection's own peer id — the key OTHER peers announce their voice count under. */
  get selfPeerId() { return this.#peerId; }

  /**
   * Σ of the OTHER peers' announced synced-voice counts (never self). One browser tab = one connection
   * but may host N synced voices, so the badge sums announced voice-counts here instead of counting
   * connections (`peerCount`) — a multi-orbiter tab then counts as its true N, not 1.
   */
  get remoteSyncedVoiceCount() {
    let sum = 0;
    for (const n of this.#peerVoiceCounts.values()) sum += n;
    return sum;
  }

  /**
   * Record a peer's announced synced-voice count (rides the room pulse's `sync:beat` relay, decoded in
   * sharedClock). Ignores self and malformed counts; re-renders the badge only on a real change.
   * @param {string} peerId — the announcing connection's peer id
   * @param {number} count — its synced-voice count (non-negative integer)
   */
  recordPeerVoiceCount(peerId, count) {
    if (!peerId || peerId === this.#peerId) return; // never count self
    if (!this.#peers.has(peerId)) return; // only LIVE peers — a stray/late announce can't resurrect a
                                          // peer we already dropped on sync:peer-left (the heartbeat
                                          // re-announces once a genuine peer is in the set)
    const n = Number(count);
    if (!Number.isInteger(n) || n < 0) return; // drop malformed announces
    if (this.#peerVoiceCounts.get(peerId) === n) return; // no change → no churn
    this.#peerVoiceCounts.set(peerId, n);
    this.#peerCountCallback?.(this.#peers.size); // re-render via the existing peers status fan-out
  }

  destroy() {
    this.#destroyed = true;
    clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
    this.#send({ type: 'sync:leave', sessionId: this.#sessionId });
    this.#detachSocket();
    this.#peers.clear();
    this.#peerVoiceCounts.clear();
    this.#isConductor = false;
    this.#knownConductorId = null;
    this.#joinedExistingSession = false;
    this.#sendQueue = [];
    this.#lastJoinedMessage = null;
    this.#rawMessageCallbacks.clear();
  }

  // ── private ──────────────────────────────────────────────────────────────

  /** Exponential backoff for the Nth retry, capped, plus a little jitter to de-synchronise tabs. */
  #reconnectDelay() {
    const step = RECONNECT_DELAY_MS * 2 ** Math.max(0, this.#reconnectAttempts - 1);
    return Math.min(step, MAX_RECONNECT_DELAY_MS) + Math.floor(Math.random() * RECONNECT_JITTER_MS);
  }

  #openSocket() {
    // Detach the prior socket's handlers before replacing it, so a stale socket's late onclose can never
    // resurrect a connection we've moved on from (e.g. a fast off→on toggle, or an auto-reconnect).
    this.#detachSocket();
    let socket;
    try {
      socket = new WebSocket(this.#wsUrl);
      this.#ws = socket;
    } catch (e) {
      console.warn('[SyncAdapter:WS] Failed to open WebSocket:', e);
      return;
    }

    socket.onopen = () => {
      // The attempt budget is NOT refunded here. Reaching `open` only proves the socket handshook,
      // and a socket that dies a moment later has recovered nothing — refunding on open is what let
      // a flapping connection retry forever. onclose decides, once it can see how long we lasted.
      this.#openedAt = Date.now();
      this.#registered = false;
      // Step 1: register as a sync-only connection.
      // Server responds with sync:registered; sync:join is sent from #onMessage.
      // When the identity is still settling (the bounded logged-in-account resolution on a first
      // connect), the register WAITS for it rather than racing it — registering earlier would
      // seat a logged-in user under the guest fallback name for the room session's whole life.
      // The promise settles exactly once (timeout ⇒ guest), so reconnect registers go out
      // immediately; failure counts as settled (guest fallback).
      const ready = this.#identity?.whenReady;
      if (ready && typeof ready.then === 'function') {
        const send = () => this.#sendRegister(socket);
        ready.then(send, send);
      } else {
        this.#sendRegister(socket);
      }
    };

    socket.onmessage = (event) => {
      try {
        this.#onMessage(JSON.parse(event.data));
      } catch (e) {
        console.warn('[SyncAdapter:WS] Failed to parse message:', e);
      }
    };

    socket.onclose = () => {
      // Only the CURRENT live socket may schedule a reconnect, and never after disconnect()/destroy().
      if (this.#destroyed || socket !== this.#ws) return;
      // A connection that held for a meaningful stretch was a real recovery, so it earns a fresh
      // budget. One that came and went inside that window was part of the same failure and keeps
      // spending the budget it was already on — that is what makes the limit reachable.
      const stayedUp = this.#openedAt > 0 && Date.now() - this.#openedAt >= CONNECTION_STABLE_MS;
      if (stayedUp) this.#reconnectAttempts = 0;
      this.#openedAt = 0;
      if (this.#reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        this.#reconnectAttempts++;
        const delay = this.#reconnectDelay();
        console.info(`[SyncAdapter:WS] Reconnecting (attempt ${this.#reconnectAttempts}) in ${delay}ms…`);
        this.#reconnectTimer = setTimeout(() => {
          this.#reconnectTimer = null;
          if (this.#destroyed) return; // a disconnect()/destroy() landed while the reconnect was pending
          this.#openSocket();
        }, delay);
      } else {
        console.warn('[SyncAdapter:WS] Max reconnect attempts reached.');
        // Terminal: no more reconnects will be tried. Tell the raw-tee riders (room client) via a
        // synthetic frame so they can release what they hold (input gates, covers) — a dead socket
        // must not leave the app locked as if a live session were still in charge.
        for (const cb of this.#rawMessageCallbacks) {
          try {
            cb({ type: 'sync:connection-lost' });
          } catch (e) {
            console.warn('[SyncAdapter:WS] raw subscriber threw:', e);
          }
        }
      }
    };

    socket.onerror = (e) => {
      console.warn('[SyncAdapter:WS] WebSocket error:', e);
    };
  }

  /**
   * Re-send `sync:register` on the LIVE socket with the identity's current getter values — for
   * an identity that upgraded after the first register (e.g. the account name resolved past the
   * bounded wait). The relay treats a re-register with the same clientId + reclaimToken as a
   * rebind and re-runs its display-name assignment for a changed base name. No-op when the
   * socket isn't open (the next reconnect register carries the upgraded identity anyway).
   */
  resendRegister() {
    if (this.#ws?.readyState === WebSocket.OPEN) this.#sendRegister(this.#ws);
  }

  /**
   * Send `sync:register` on `socket` — skipped when the socket was replaced or closed while the
   * identity was settling (the replacement socket's own onopen registers instead).
   */
  #sendRegister(socket) {
    if (socket !== this.#ws || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({
      type: 'sync:register',
      peerId: this.#peerId,       // app metadata for conductor election
      joinedAt: this.#joinedAt,   // app metadata for conductor election
      // Room identity (absent outside room mode). Fields are read at SEND time (the identity
      // object may be getter-backed), so a reconnect register presents whatever the tab holds
      // NOW — including a reclaimToken adopted from an earlier ack. The token, not the visible
      // clientId, is what proves the seat on a reconnect-reclaim.
      ...(this.#identity ? {
        clientId: this.#identity.clientId,
        displayName: this.#identity.displayName ?? null,
        userId: this.#identity.userId ?? null,
        ...(this.#identity.reclaimToken ? { reclaimToken: this.#identity.reclaimToken } : {}),
      } : {}),
    }));
  }

  /** Detach the current socket's handlers + close it, so its async onclose can't drive our lifecycle. */
  #detachSocket() {
    const ws = this.#ws;
    if (!ws) return;
    ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
    try { ws.close(); } catch { /* already closing/closed */ }
    this.#ws = null;
  }

  // Send a typed control message (sync:leave) immediately if the socket is open; else drop — it's
  // transient. (Buffered-until-join replication rides `sendRaw`/`#sendQueue`, flushed on sync:registered.)
  #send(msg) {
    if (this.#ws?.readyState === WebSocket.OPEN) {
      try {
        this.#ws.send(JSON.stringify(msg));
      } catch (e) {
        console.warn('[SyncAdapter:WS] send failed:', e);
      }
    }
  }

  #onMessage(msg) {
    // Tee: fan every inbound parsed message to raw subscribers BEFORE the
    // typed handling (additive — the switch below is unchanged). Isolated so a
    // throwing subscriber can never break the existing sync path.
    if (this.#rawMessageCallbacks.size) {
      for (const cb of this.#rawMessageCallbacks) {
        try {
          cb(msg);
        } catch (e) {
          console.warn('[SyncAdapter:WS] raw subscriber threw:', e);
        }
      }
    }

    switch (msg.type) {
      case 'sync:registered': {
        // Server confirmed our sync-only connection; now join the session
        this.#registered = true;
        this.#ws.send(JSON.stringify({
          type: 'sync:join',
          sessionId: this.#sessionId,
          peerId: this.#peerId,
          joinedAt: this.#joinedAt,
        }));
        // Flush any messages that were queued before registration
        const queued = this.#sendQueue.splice(0);
        queued.forEach((m) => this.#ws.send(JSON.stringify(m)));
        break;
      }

      case 'sync:joined': {
        // Kept so a tee subscriber built AFTER the join still learns it (see onRawMessage).
        this.#lastJoinedMessage = msg;
        // Server confirms session join; receive list of existing peers
        const snapshot = Array.isArray(msg.peers) ? msg.peers : [];
        snapshot.forEach(({ peerId, joinedAt }) => {
          if (peerId !== this.#peerId) this.#peers.set(peerId, joinedAt);
        });
        // Reconcile voice counts to this authoritative snapshot: drop any peer not present (covers a
        // sync:peer-left we missed while the socket was down on an auto-reconnect). Present peers
        // re-announce on their heartbeat, so their counts repopulate.
        const present = new Set(snapshot.map((p) => p.peerId));
        for (const id of this.#peerVoiceCounts.keys()) {
          if (!present.has(id)) this.#peerVoiceCounts.delete(id);
        }
        this.#joinedExistingSession = this.#peers.size > 0;
        this.#evaluateConductor();
        this.#peerCountCallback?.(this.#peers.size);
        break;
      }

      case 'sync:peer-joined': {
        if (msg.peerId && msg.peerId !== this.#peerId) {
          this.#peers.set(msg.peerId, msg.joinedAt);
          // force: re-elect even if we already self-elected. Without the (now-removed) conductor
          // heartbeat carrying isConductor, this is the only signal that demotes a peer which elected
          // itself in a near-simultaneous join (both saw an empty peer list) once it learns of an
          // older peer — so the oldest joinedAt is the single conductor (matches BroadcastChannelAdapter).
          this.#evaluateConductor({ force: true });
          this.#peerCountCallback?.(this.#peers.size);
        }
        break;
      }

      case 'sync:peer-left': {
        this.#peers.delete(msg.peerId);
        this.#peerVoiceCounts.delete(msg.peerId); // drop its badge contribution

        if (this.#knownConductorId === msg.peerId) {
          this.#knownConductorId = null;
          this.#joinedExistingSession = false;
        }
        if (this.#peers.size === 0) {
          this.#joinedExistingSession = false;
        }
        this.#evaluateConductor({ force: true });
        this.#peerCountCallback?.(this.#peers.size);
        break;
      }

      default:
        break;
    }
  }

  #evaluateConductor({ force = false } = {}) {
    if (!force && this.#isConductor) return;
    if (this.#knownConductorId && this.#knownConductorId !== this.#peerId) {
      this.#isConductor = false;
      return;
    }
    if (this.#joinedExistingSession && this.#peers.size > 0) {
      this.#isConductor = false;
      return;
    }
    const all = [[this.#peerId, this.#joinedAt], ...this.#peers.entries()];
    const [conductorId] = all.reduce((min, curr) => (
      this.#compareConductorCandidate(curr, min) < 0 ? curr : min
    ));
    this.#isConductor = conductorId === this.#peerId;
    this.#knownConductorId = conductorId;
  }

  #compareConductorCandidate(a, b) {
    const joinedDelta = Number(a[1]) - Number(b[1]);
    if (joinedDelta !== 0) return joinedDelta;
    return String(a[0]).localeCompare(String(b[0]));
  }
}
